import {
  CheckIcon,
  LockClosedIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid';
import { useEffect } from 'react';
import { Button, Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RouterPaths } from '../../../../components/Router/Router.constants';
import { useSubscriptionAnalytics } from 'src/hooks/analytics/subscription/useSubscriptionAnalytics.hook';
import { SettingsTabs } from '../../../../pages/Settings/Settings.enum';
import { useModalsContext } from '../../../ModalsProvider/Modals.context';
import { useSubscriptionContext } from '../../Subscription.context';

export default function SubscribeIntegrationsModal() {
  const { showSubscribeIntegrationsModal, setShowSubscribeIntegrationsModal } =
    useModalsContext();
  const navigate = useNavigate();
  const { t } = useTranslation('subscription/modals');
  const { setUpInfo, getPlans } = useSubscriptionContext();
  const { trackPurchaseCta } = useSubscriptionAnalytics();

  // BUG-868: on active workspaces nothing auto-fetches the plans, so setUpInfo
  // was always undefined here and the title interpolated a blank price ("$ *").
  // The modal mounts on open (ModalsProvider), so fetch on mount; the RET-1190
  // concurrency guard in getPlans absorbs repeated opens.
  useEffect(() => {
    if (!setUpInfo) void getPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      open={showSubscribeIntegrationsModal}
      onClose={() => setShowSubscribeIntegrationsModal(false)}
      className="SubscribeIntegrationsModal"
    >
      <Modal.Body className="SubscribeIntegrationsModal__content">
        <div className="SubscribeIntegrationsModal__left_side">
          {/* This button is only visible on sm screens using tailwind */}
          <Button
            shape="circle"
            size="sm"
            color="ghost"
            variant="outline"
            className="SubscribeIntegrationsModal__left_side__button"
            onClick={() => setShowSubscribeIntegrationsModal(false)}
          >
            <XMarkIcon className="SubscribeIntegrationsModal__right_side__icon" />
          </Button>
          <div className="SubscribeIntegrationsModal__left_side__warning">
            <LockClosedIcon className="w-6 h-6" />
            <p className="text-sm font-medium">
              {t('integrations.subscriptionWarning')}
            </p>
          </div>
          <p className="SubscribeIntegrationsModal__left_side__title">
            {setUpInfo ? (
              <Trans
                i18nKey="subscription/modals:integrations.title"
                components={{ span: <span className="text-accent" /> }}
                values={{
                  setUpPrice: setUpInfo.trialCost,
                  currency: setUpInfo.currency,
                }}
              />
            ) : (
              // Sin datos de planes el título con precio interpola "$ " en
              // blanco — se usa la variante sin cifras (BUG-868).
              <Trans
                i18nKey="subscription/modals:integrations.titleNoPrice"
                components={{ span: <span className="text-accent" /> }}
              />
            )}
          </p>
          <div className=" overflow-auto h-[300px] sm:h-full">
            <div className="SubscribeIntegrationsModal__left_side__features">
              <p>{t('integrations.subtitle')}</p>
              <div className="SubscribeIntegrationsModal__left_side__feature">
                <CheckIcon className="w-6 h-6" />
                <p>
                  <Trans
                    i18nKey="subscription/modals:integrations.features.feature1"
                    components={{ span: <span className="font-bold" /> }}
                  />
                </p>
              </div>
              <div className="SubscribeIntegrationsModal__left_side__feature">
                <CheckIcon className="w-6 h-6" />
                <p>
                  <Trans
                    i18nKey="subscription/modals:integrations.features.feature2"
                    components={{ span: <span className="font-bold" /> }}
                  />
                </p>
              </div>
              <div className="SubscribeIntegrationsModal__left_side__feature">
                <CheckIcon className="w-8 h-8" />
                <p>
                  <Trans
                    i18nKey="subscription/modals:integrations.features.feature3"
                    components={{ span: <span className="font-bold" /> }}
                  />
                </p>
              </div>
            </div>
            {setUpInfo && (
              <p className="text-xs mt-4">
                <Trans
                  i18nKey="subscription/modals:integrations.disclaimer"
                  components={{
                    b: <b />,
                    br: <br />,
                  }}
                  values={{
                    fullPrice: setUpInfo.monthlyPrice.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    }),
                    currency: setUpInfo.currency,
                  }}
                />
              </p>
            )}
          </div>
          <Button
            color="primary"
            className="w-full"
            onClick={() => {
              navigate(
                `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&cta_type=ConnectIntegrations`,
              );
              setShowSubscribeIntegrationsModal(false);
              trackPurchaseCta('modal', 'ConnectIntegrations');
            }}
          >
            Activa tu plan hoy
          </Button>
        </div>
        <div className="SubscribeIntegrationsModal__right_side">
          <Button
            shape="circle"
            size="sm"
            color="ghost"
            className="SubscribeIntegrationsModal__right_side__closeButton"
            onClick={() => setShowSubscribeIntegrationsModal(false)}
          >
            <XMarkIcon className="SubscribeIntegrationsModal__right_side__icon" />
          </Button>
          <img
            src="/images/subscriptions/IntegrationsSetUp.png"
            alt="leadsales-logo"
            className="SubscribeIntegrationsModal__right_side__image"
          />
        </div>
      </Modal.Body>
    </Modal>
  );
}
